import React from 'react';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { INPUT_THEMES, INPUT_SIZES, type InputTheme, type InputSize } from './inputThemes';

interface ThemedSelectTriggerProps extends React.ComponentPropsWithoutRef<typeof SelectTrigger> {
  theme?: InputTheme;
  size?: InputSize;
}

/**
 * Themed Select Trigger that uses design-system tokens for color/size.
 *
 * Usage (inside Select component):
 *   <Select value={value} onValueChange={onChange}>
 *     <ThemedSelectTrigger theme="dark" size="md">
 *       <SelectValue placeholder="Choose..." />
 *     </ThemedSelectTrigger>
 *     <SelectContent theme="dark">
 *       {items.map(item => (
 *         <ThemedSelectItem key={item} value={item} theme="dark">{item}</ThemedSelectItem>
 *       ))}
 *     </SelectContent>
 *   </Select>
 */
export const ThemedSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectTrigger>,
  ThemedSelectTriggerProps
>(({ theme = 'dark', size = 'md', className, ...props }, ref) => {
  const sk = INPUT_SIZES[size];
  const tk = INPUT_THEMES[theme];

  // MIGRATION NOTE (2026-03-25):
  // SelectTrigger base is now "clean" (no hardcoded border-white bg-white/10).
  // This allows us to use pure Tailwind classes without inline styles.
  // The design system (INPUT_THEMES) is the single source of truth.
  return (
    <SelectTrigger
      ref={ref}
      className={[
        sk.base,
        tk.select,  // Now contains all styling: border, bg, color, font-size, etc.
        'rounded-lg font-sans focus:outline-none focus-visible:ring-1 focus-visible:ring-red-700 focus-visible:ring-offset-0',
        '[&>span]:line-clamp-1',
        tk.chevronClass,
        className ?? '',
      ].filter(Boolean).join(' ')}
      {...props}
    />
  );
});

ThemedSelectTrigger.displayName = 'ThemedSelectTrigger';

interface ThemedSelectContentProps extends React.ComponentPropsWithoutRef<typeof SelectContent> {
  theme?: InputTheme;
}

/**
 * Themed Select Content that uses theme tokens for dropdown styling.
 */
export const ThemedSelectContent = React.forwardRef<
  React.ElementRef<typeof SelectContent>,
  ThemedSelectContentProps
>(({ theme = 'dark', className, ...props }, ref) => {
  const tk = INPUT_THEMES[theme];

  return (
    <SelectContent
      ref={ref}
      className={[tk.dropdown, className ?? ''].filter(Boolean).join(' ')}
      {...props}
    />
  );
});

ThemedSelectContent.displayName = 'ThemedSelectContent';

interface ThemedSelectItemProps extends React.ComponentPropsWithoutRef<typeof SelectItem> {
  theme?: InputTheme;
}

/**
 * Themed Select Item that uses theme tokens for item styling.
 */
export const ThemedSelectItem = React.forwardRef<
  React.ElementRef<typeof SelectItem>,
  ThemedSelectItemProps
>(({ theme = 'dark', className, ...props }, ref) => {
  const tk = INPUT_THEMES[theme];

  return (
    <SelectItem
      ref={ref}
      className={[tk.dropdownItem, className ?? ''].filter(Boolean).join(' ')}
      {...props}
    />
  );
});

ThemedSelectItem.displayName = 'ThemedSelectItem';
